import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, MapPin, Loader2, Save, LogOut, Plus, X, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fileUrl } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { StarRating } from "@/components/StarRating";
import { PostCard } from "@/components/PostCard";
import { InviteFamily } from "@/components/InviteFamily";
import { TrustBadge } from "@/components/TrustBadge";
import { TrustCard } from "@/components/TrustCard";
import { useAuth } from "@/context/AuthContext";

export default function Profile() {
  const navigate = useNavigate();
  const { user, setUser, logout, refreshMe } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", bio: "", phone: "", address: "", interests: [] });
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [interestInput, setInterestInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [myPosts, setMyPosts] = useState([]);

  useEffect(() => {
    if (user) {
      setForm({ name: user.name || "", bio: user.bio || "", phone: user.phone || "", address: user.address || "", interests: user.interests || [] });
      setCoords({ lat: user.lat ?? null, lng: user.lng ?? null });
    }
    api.get("/my/posts").then((r) => setMyPosts(r.data)).catch(() => {});
  }, [user]);

  const addInterest = () => {
    const v = interestInput.trim();
    if (v && !form.interests.includes(v)) setForm({ ...form, interests: [...form.interests, v] });
    setInterestInput("");
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser({ ...user, picture: data.picture });
      toast.success("Photo updated");
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Location not supported");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); toast.success("Location captured"); },
      () => { setLocating(false); toast.error("Couldn't get location"); }
    );
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/profile", { ...form, lat: coords.lat, lng: coords.lng });
      await refreshMe();
      toast.success("Profile saved");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="font-head text-3xl sm:text-4xl font-bold text-forest-900 mb-6">Your profile</h1>

      <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-5">
          <div className="relative">
            <Avatar user={{ ...user, picture: user?.picture }} size={96} />
            <button onClick={() => fileRef.current?.click()} data-testid="upload-photo-btn"
              className="absolute -bottom-1 -right-1 w-11 h-11 rounded-full bg-terracotta text-white flex items-center justify-center border-4 border-white hover:bg-terracotta-dark transition-colors">
              {uploading ? <Loader2 size={20} className="animate-spin" /> : <Camera size={20} strokeWidth={2.5} />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="photo-input" />
          </div>
          <div>
            <p className="font-head text-2xl font-bold text-forest-900">{user?.name}</p>
            <p className="text-forest-500">{user?.email}</p>
            <div className="mt-1 flex items-center gap-2 flex-wrap"><StarRating value={user?.rating_avg} count={user?.rating_count} size={16} /><TrustBadge level={user?.trust_level || "unverified"} size="sm" /></div>
          </div>
        </div>

        <div className="space-y-4 mt-7">
          <div>
            <label className="font-bold text-forest-900 mb-1.5 block">Name</label>
            <input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full min-h-[56px] text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-cream" />
          </div>
          <div>
            <label className="font-bold text-forest-900 mb-1.5 block">About you</label>
            <textarea data-testid="profile-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3}
              placeholder="Tell the community a little about yourself"
              className="w-full text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-cream resize-none" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-bold text-forest-900 mb-1.5 block">Phone (optional)</label>
              <input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full min-h-[56px] text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta outline-none bg-cream" />
            </div>
            <div>
              <label className="font-bold text-forest-900 mb-1.5 block">Neighbourhood</label>
              <input data-testid="profile-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full min-h-[56px] text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta outline-none bg-cream" />
            </div>
          </div>
          <button type="button" onClick={useMyLocation} data-testid="profile-location-btn"
            className={`min-h-[52px] px-5 rounded-full font-semibold border-2 flex items-center gap-2 transition-colors ${coords.lat != null ? "bg-forest-700 text-white border-forest-700" : "bg-white text-forest-700 border-border"}`}>
            {locating ? <Loader2 size={20} className="animate-spin" /> : <MapPin size={20} strokeWidth={2.5} />}
            {coords.lat != null ? "Location set ✓ (used for nearby matching)" : "Set my location"}
          </button>

          <div>
            <label className="font-bold text-forest-900 mb-1.5 block">Interests</label>
            <div className="flex gap-2">
              <input data-testid="interest-input" value={interestInput} onChange={(e) => setInterestInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addInterest(); } }}
                placeholder="e.g. Gardening, Chess" className="flex-1 min-h-[52px] text-lg px-4 rounded-xl border-2 border-border focus:border-terracotta outline-none bg-cream" />
              <button type="button" onClick={addInterest} data-testid="add-interest" className="w-[52px] h-[52px] rounded-xl bg-forest-700 text-white flex items-center justify-center"><Plus size={24} strokeWidth={2.5} /></button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {form.interests.map((it) => (
                <span key={it} className="px-4 py-2 rounded-full bg-gold-light border-2 border-gold text-forest-900 font-semibold flex items-center gap-2">
                  {it}<button onClick={() => setForm({ ...form, interests: form.interests.filter((x) => x !== it) })}><X size={16} strokeWidth={3} /></button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <button onClick={save} disabled={busy} data-testid="save-profile"
          className="w-full min-h-[56px] mt-6 rounded-full bg-terracotta text-white font-bold text-lg hover:bg-terracotta-dark transition-transform active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2">
          <Save size={22} strokeWidth={2.5} /> {busy ? "Saving…" : "Save profile"}
        </button>
      </div>

      <div className="mt-6">
        <h2 className="font-head text-2xl font-bold text-forest-900 mb-4 flex items-center gap-2">
          <ClipboardList size={26} strokeWidth={2.5} className="text-terracotta" /> Your posts
        </h2>
        {myPosts.length === 0 ? (
          <p className="text-forest-700 bg-white rounded-2xl border-2 border-border p-6">You haven't posted yet.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2" data-testid="my-posts">{myPosts.map((p) => <PostCard key={p.post_id} post={p} />)}</div>
        )}
      </div>

      <div className="mt-6"><TrustCard /></div>

      <div className="mt-6">
        <InviteFamily />
      </div>

      <button onClick={handleLogout} data-testid="profile-logout"
        className="w-full min-h-[56px] mt-6 rounded-full bg-white border-2 border-border text-destructive font-bold text-lg hover:bg-destructive hover:text-white transition-colors flex items-center justify-center gap-2">
        <LogOut size={22} strokeWidth={2.5} /> Log out
      </button>
    </div>
  );
}
